export const ROUTE_PATHS = {
  HOME: '/',
  ABOUT: '/about',
  SERVICES: '/services',
  EXPERTISE: '/expertise',
  CONTACT: '/contact'
} as const;

export type Language = 'fr' | 'en' | 'es' | 'zh' | 'ar';

export interface Translation {
  nav: {
    home: string;
    about: string;
    services: string;
    expertise: string;
    contact: string;
  };
  hero: {
    title: string;
    subtitle: string;
    cta: string;
    experience: string;
  };
  about: {
    title: string;
    subtitle: string;
    description: string;
    founder: string;
    founderTitle: string;
    mission: string;
    missionText: string;
    vision: string;
    visionText: string;
    values: string;
    valuesText: string;
  };
  services: {
    title: string;
    subtitle: string;
    customsClearance: {
      title: string;
      description: string;
    };
    transitOperations: {
      title: string;
      description: string;
    };
    compliance: {
      title: string;
      description: string;
    };
    consulting: {
      title: string;
      description: string;
    };
  };
  expertise: {
    title: string;
    subtitle: string;
    years: string;
    countries: string;
    asia: string;
    europe: string;
    africa: string;
  };
  contact: {
    title: string;
    subtitle: string;
    phone: string;
    email: string;
    getInTouch: string;
    name: string;
    message: string;
    send: string;
  };
  footer: {
    copyright: string;
    allRightsReserved: string;
    about: string;
    quickLinks: string;
    followUs: string;
  };
}

export const translations: Record<Language, Translation> = {
  fr: {
    nav: {
      home: 'Accueil',
      about: 'À propos',
      services: 'Services',
      expertise: 'Expertise',
      contact: 'Contact'
    },
    hero: {
      title: 'Solutions Douanières & Transit International',
      subtitle: 'Plus de 20 ans d\'expertise en facilitation des opérations douanières et de transit à travers l\'Asie, l\'Europe et l\'Afrique.',
      cta: 'Contactez-nous',
      experience: '20+ ans d\'expérience'
    },
    about: {
      title: 'À propos de Kyra AVM Consulting',
      subtitle: 'Votre partenaire de confiance en solutions douanières',
      description: 'Kyra AVM Consulting est un cabinet spécialisé dans les solutions douanières et de transit international. Nous facilitons les opérations commerciales transfrontalières avec une expertise approfondie des réglementations douanières mondiales.',
      founder: 'Monsieur Hamir Valdez',
      founderTitle: 'Fondateur & Expert en Douane',
      mission: 'Notre Mission',
      missionText: 'Simplifier et accélérer les opérations douanières pour nos clients en offrant des solutions sur mesure, conformes aux réglementations internationales, tout en optimisant les coûts et les délais.',
      vision: 'Notre Vision',
      visionText: 'Devenir le partenaire de référence en solutions douanières et de transit pour les entreprises opérant sur les marchés asiatiques, européens et africains.',
      values: 'Nos Valeurs',
      valuesText: 'Excellence, Intégrité, Expertise, Réactivité et Engagement envers nos clients.'
    },
    services: {
      title: 'Nos Services',
      subtitle: 'Solutions complètes pour vos opérations douanières et de transit',
      customsClearance: {
        title: 'Dédouanement',
        description: 'Gestion complète des procédures de dédouanement import/export. Préparation et soumission de toutes les déclarations douanières. Optimisation des classifications tarifaires et régimes douaniers.'
      },
      transitOperations: {
        title: 'Opérations de Transit',
        description: 'Coordination des opérations de transit international. Gestion des documents de transport et suivi des expéditions. Optimisation des routes et des délais de livraison.'
      },
      compliance: {
        title: 'Conformité Réglementaire',
        description: 'Veille réglementaire et mise en conformité douanière. Audit des procédures et identification des risques. Formation des équipes aux réglementations douanières.'
      },
      consulting: {
        title: 'Conseil Stratégique',
        description: 'Analyse et optimisation des flux logistiques internationaux. Conseil en stratégie d\'importation/exportation. Négociation avec les autorités douanières.'
      }
    },
    expertise: {
      title: 'Notre Expertise Internationale',
      subtitle: 'Plus de 20 ans d\'expérience dans les opérations douanières à travers 3 continents',
      years: 'Années d\'expérience',
      countries: 'Pays couverts',
      asia: 'Asie',
      europe: 'Europe',
      africa: 'Afrique'
    },
    contact: {
      title: 'Contactez-nous',
      subtitle: 'Discutons de vos besoins en solutions douanières',
      phone: 'Téléphone',
      email: 'Email',
      getInTouch: 'Prenons contact pour faciliter vos opérations douanières',
      name: 'Nom',
      message: 'Message',
      send: 'Envoyer'
    },
    footer: {
      copyright: '© 2026 Kyra AVM Consulting',
      allRightsReserved: 'Tous droits réservés',
      about: 'À propos',
      quickLinks: 'Liens rapides',
      followUs: 'Suivez-nous'
    }
  },
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      expertise: 'Expertise',
      contact: 'Contact'
    },
    hero: {
      title: 'Customs & International Transit Solutions',
      subtitle: 'Over 20 years of expertise in facilitating customs and transit operations across Asia, Europe and Africa.',
      cta: 'Contact us',
      experience: '20+ years of experience'
    },
    about: {
      title: 'About Kyra AVM Consulting',
      subtitle: 'Your trusted partner in customs solutions',
      description: 'Kyra AVM Consulting is a firm specializing in customs and international transit solutions. We facilitate cross-border trade operations with deep expertise in global customs regulations.',
      founder: 'Mr. Hamir Valdez',
      founderTitle: 'Founder & Customs Expert',
      mission: 'Our Mission',
      missionText: 'To simplify and accelerate customs operations for our clients by offering tailored solutions that comply with international regulations while optimizing costs and timelines.',
      vision: 'Our Vision',
      visionText: 'To become the reference partner in customs and transit solutions for companies operating in Asian, European and African markets.',
      values: 'Our Values',
      valuesText: 'Excellence, Integrity, Expertise, Responsiveness and Commitment to our clients.'
    },
    services: {
      title: 'Our Services',
      subtitle: 'Complete solutions for your customs and transit operations',
      customsClearance: {
        title: 'Customs Clearance',
        description: 'Complete management of import/export customs clearance procedures. Preparation and submission of all customs declarations. Optimization of tariff classifications and customs regimes.'
      },
      transitOperations: {
        title: 'Transit Operations',
        description: 'Coordination of international transit operations. Management of transport documents and shipment tracking. Optimization of routes and delivery times.'
      },
      compliance: {
        title: 'Regulatory Compliance',
        description: 'Regulatory monitoring and customs compliance. Audit of procedures and risk identification. Training of teams in customs regulations.'
      },
      consulting: {
        title: 'Strategic Consulting',
        description: 'Analysis and optimization of international logistics flows. Import/export strategy consulting. Negotiation with customs authorities.'
      }
    },
    expertise: {
      title: 'Our International Expertise',
      subtitle: 'Over 20 years of experience in customs operations across 3 continents',
      years: 'Years of experience',
      countries: 'Countries covered',
      asia: 'Asia',
      europe: 'Europe',
      africa: 'Africa'
    },
    contact: {
      title: 'Contact us',
      subtitle: 'Let\'s discuss your customs solutions needs',
      phone: 'Phone',
      email: 'Email',
      getInTouch: 'Let\'s connect to facilitate your customs operations',
      name: 'Name',
      message: 'Message',
      send: 'Send'
    },
    footer: {
      copyright: '© 2026 Kyra AVM Consulting',
      allRightsReserved: 'All rights reserved',
      about: 'About',
      quickLinks: 'Quick links',
      followUs: 'Follow us'
    }
  },
  es: {
    nav: {
      home: 'Inicio',
      about: 'Acerca de',
      services: 'Servicios',
      expertise: 'Experiencia',
      contact: 'Contacto'
    },
    hero: {
      title: 'Soluciones Aduaneras y Tránsito Internacional',
      subtitle: 'Más de 20 años de experiencia facilitando operaciones aduaneras y de tránsito en Asia, Europa y África.',
      cta: 'Contáctenos',
      experience: '20+ años de experiencia'
    },
    about: {
      title: 'Acerca de Kyra AVM Consulting',
      subtitle: 'Su socio de confianza en soluciones aduaneras',
      description: 'Kyra AVM Consulting es una firma especializada en soluciones aduaneras y de tránsito internacional. Facilitamos operaciones comerciales transfronterizas con profunda experiencia en regulaciones aduaneras globales.',
      founder: 'Sr. Hamir Valdez',
      founderTitle: 'Fundador y Experto en Aduanas',
      mission: 'Nuestra Misión',
      missionText: 'Simplificar y acelerar las operaciones aduaneras para nuestros clientes ofreciendo soluciones personalizadas que cumplan con las regulaciones internacionales mientras optimizamos costos y plazos.',
      vision: 'Nuestra Visión',
      visionText: 'Convertirnos en el socio de referencia en soluciones aduaneras y de tránsito para empresas que operan en mercados asiáticos, europeos y africanos.',
      values: 'Nuestros Valores',
      valuesText: 'Excelencia, Integridad, Experiencia, Capacidad de respuesta y Compromiso con nuestros clientes.'
    },
    services: {
      title: 'Nuestros Servicios',
      subtitle: 'Soluciones completas para sus operaciones aduaneras y de tránsito',
      customsClearance: {
        title: 'Despacho Aduanero',
        description: 'Gestión completa de procedimientos de despacho aduanero de importación/exportación. Preparación y presentación de todas las declaraciones aduaneras. Optimización de clasificaciones arancelarias y regímenes aduaneros.'
      },
      transitOperations: {
        title: 'Operaciones de Tránsito',
        description: 'Coordinación de operaciones de tránsito internacional. Gestión de documentos de transporte y seguimiento de envíos. Optimización de rutas y tiempos de entrega.'
      },
      compliance: {
        title: 'Cumplimiento Normativo',
        description: 'Monitoreo regulatorio y cumplimiento aduanero. Auditoría de procedimientos e identificación de riesgos. Capacitación de equipos en regulaciones aduaneras.'
      },
      consulting: {
        title: 'Consultoría Estratégica',
        description: 'Análisis y optimización de flujos logísticos internacionales. Consultoría en estrategia de importación/exportación. Negociación con autoridades aduaneras.'
      }
    },
    expertise: {
      title: 'Nuestra Experiencia Internacional',
      subtitle: 'Más de 20 años de experiencia en operaciones aduaneras en 3 continentes',
      years: 'Años de experiencia',
      countries: 'Países cubiertos',
      asia: 'Asia',
      europe: 'Europa',
      africa: 'África'
    },
    contact: {
      title: 'Contáctenos',
      subtitle: 'Hablemos de sus necesidades en soluciones aduaneras',
      phone: 'Teléfono',
      email: 'Correo electrónico',
      getInTouch: 'Conectemos para facilitar sus operaciones aduaneras',
      name: 'Nombre',
      message: 'Mensaje',
      send: 'Enviar'
    },
    footer: {
      copyright: '© 2026 Kyra AVM Consulting',
      allRightsReserved: 'Todos los derechos reservados',
      about: 'Acerca de',
      quickLinks: 'Enlaces rápidos',
      followUs: 'Síguenos'
    }
  },
  zh: {
    nav: {
      home: '首页',
      about: '关于我们',
      services: '服务',
      expertise: '专业知识',
      contact: '联系我们'
    },
    hero: {
      title: '海关与国际运输解决方案',
      subtitle: '在亚洲、欧洲和非洲拥有超过20年的海关和运输业务便利化经验。',
      cta: '联系我们',
      experience: '20+年经验'
    },
    about: {
      title: '关于 Kyra AVM Consulting',
      subtitle: '您值得信赖的海关解决方案合作伙伴',
      description: 'Kyra AVM Consulting 是一家专门从事海关和国际运输解决方案的公司。我们凭借对全球海关法规的深入专业知识，促进跨境贸易运营。',
      founder: 'Hamir Valdez 先生',
      founderTitle: '创始人兼海关专家',
      mission: '我们的使命',
      missionText: '通过提供符合国际法规的定制解决方案，简化和加速客户的海关业务，同时优化成本和时间。',
      vision: '我们的愿景',
      visionText: '成为在亚洲、欧洲和非洲市场运营的企业在海关和运输解决方案方面的首选合作伙伴。',
      values: '我们的价值观',
      valuesText: '卓越、诚信、专业、响应能力和对客户的承诺。'
    },
    services: {
      title: '我们的服务',
      subtitle: '为您的海关和运输业务提供完整解决方案',
      customsClearance: {
        title: '清关服务',
        description: '全面管理进出口清关程序。准备和提交所有海关申报。优化关税分类和海关制度。'
      },
      transitOperations: {
        title: '运输业务',
        description: '协调国际运输业务。管理运输文件和货物跟踪。优化路线和交货时间。'
      },
      compliance: {
        title: '合规管理',
        description: '监管监控和海关合规。程序审计和风险识别。海关法规团队培训。'
      },
      consulting: {
        title: '战略咨询',
        description: '分析和优化国际物流流程。进出口战略咨询。与海关当局谈判。'
      }
    },
    expertise: {
      title: '我们的国际专业知识',
      subtitle: '在3大洲拥有超过20年的海关业务经验',
      years: '年经验',
      countries: '覆盖国家',
      asia: '亚洲',
      europe: '欧洲',
      africa: '非洲'
    },
    contact: {
      title: '联系我们',
      subtitle: '让我们讨论您的海关解决方案需求',
      phone: '电话',
      email: '电子邮件',
      getInTouch: '让我们联系以促进您的海关业务',
      name: '姓名',
      message: '消息',
      send: '发送'
    },
    footer: {
      copyright: '© 2026 Kyra AVM Consulting',
      allRightsReserved: '版权所有',
      about: '关于',
      quickLinks: '快速链接',
      followUs: '关注我们'
    }
  },
  ar: {
    nav: {
      home: 'الرئيسية',
      about: 'من نحن',
      services: 'الخدمات',
      expertise: 'الخبرة',
      contact: 'اتصل بنا'
    },
    hero: {
      title: 'حلول الجمارك والعبور الدولي',
      subtitle: 'أكثر من 20 عامًا من الخبرة في تسهيل عمليات الجمارك والعبور عبر آسيا وأوروبا وأفريقيا.',
      cta: 'اتصل بنا',
      experience: '20+ سنة من الخبرة'
    },
    about: {
      title: 'حول Kyra AVM Consulting',
      subtitle: 'شريكك الموثوق في الحلول الجمركية',
      description: 'Kyra AVM Consulting هي شركة متخصصة في الحلول الجمركية والعبور الدولي. نحن نسهل عمليات التجارة عبر الحدود بخبرة عميقة في اللوائح الجمركية العالمية.',
      founder: 'السيد حمير فالديز',
      founderTitle: 'المؤسس وخبير الجمارك',
      mission: 'مهمتنا',
      missionText: 'تبسيط وتسريع العمليات الجمركية لعملائنا من خلال تقديم حلول مخصصة تتوافق مع اللوائح الدولية مع تحسين التكاليف والجداول الزمنية.',
      vision: 'رؤيتنا',
      visionText: 'أن نصبح الشريك المرجعي في حلول الجمارك والعبور للشركات العاملة في الأسواق الآسيوية والأوروبية والأفريقية.',
      values: 'قيمنا',
      valuesText: 'التميز والنزاهة والخبرة والاستجابة والالتزام تجاه عملائنا.'
    },
    services: {
      title: 'خدماتنا',
      subtitle: 'حلول كاملة لعمليات الجمارك والعبور الخاصة بك',
      customsClearance: {
        title: 'التخليص الجمركي',
        description: 'إدارة كاملة لإجراءات التخليص الجمركي للاستيراد/التصدير. إعداد وتقديم جميع الإقرارات الجمركية. تحسين التصنيفات التعريفية والأنظمة الجمركية.'
      },
      transitOperations: {
        title: 'عمليات العبور',
        description: 'تنسيق عمليات العبور الدولي. إدارة وثائق النقل وتتبع الشحنات. تحسين المسارات وأوقات التسليم.'
      },
      compliance: {
        title: 'الامتثال التنظيمي',
        description: 'المراقبة التنظيمية والامتثال الجمركي. تدقيق الإجراءات وتحديد المخاطر. تدريب الفرق على اللوائح الجمركية.'
      },
      consulting: {
        title: 'الاستشارات الاستراتيجية',
        description: 'تحليل وتحسين تدفقات اللوجستيات الدولية. استشارات استراتيجية الاستيراد/التصدير. التفاوض مع السلطات الجمركية.'
      }
    },
    expertise: {
      title: 'خبرتنا الدولية',
      subtitle: 'أكثر من 20 عامًا من الخبرة في العمليات الجمركية عبر 3 قارات',
      years: 'سنوات الخبرة',
      countries: 'الدول المغطاة',
      asia: 'آسيا',
      europe: 'أوروبا',
      africa: 'أفريقيا'
    },
    contact: {
      title: 'اتصل بنا',
      subtitle: 'دعنا نناقش احتياجاتك من الحلول الجمركية',
      phone: 'الهاتف',
      email: 'البريد الإلكتروني',
      getInTouch: 'دعنا نتواصل لتسهيل عملياتك الجمركية',
      name: 'الاسم',
      message: 'الرسالة',
      send: 'إرسال'
    },
    footer: {
      copyright: '© 2026 Kyra AVM Consulting',
      allRightsReserved: 'جميع الحقوق محفوظة',
      about: 'من نحن',
      quickLinks: 'روابط سريعة',
      followUs: 'تابعنا'
    }
  }
};

export const CONTACT_INFO = {
  phone: '+34672543461',
  email: 'hamiravm@gmail.com'
} as const;

export const EXPERTISE_DATA = {
  asia: [
    { city: 'Bangkok', country: 'Thaïlande / Thailand / Tailandia / 泰国 / تايلاند' },
    { city: 'Hong Kong', country: 'Chine / China / China / 中国 / الصين' },
    { city: 'Jakarta', country: 'Indonésie / Indonesia / Indonesia / 印度尼西亚 / إندونيسيا' },
    { city: 'Manila', country: 'Philippines / Philippines / Filipinas / 菲律宾 / الفلبين' },
    { city: 'Guangzhou', country: 'Chine / China / China / 中国 / الصين' }
  ],
  europe: [
    { city: 'Istanbul', country: 'Turquie / Turkey / Turquía / 土耳其 / تركيا' },
    { city: 'Paris', country: 'France / France / Francia / 法国 / فرنسا' },
    { city: 'Madrid', country: 'Espagne / Spain / España / 西班牙 / إسبانيا' },
    { city: 'Bruxelles', country: 'Belgique / Belgium / Bélgica / 比利时 / بلجيكا' },
    { city: 'Londres', country: 'Royaume-Uni / UK / Reino Unido / 英国 / المملكة المتحدة' },
    { city: 'Milan', country: 'Italie / Italy / Italia / 意大利 / إيطاليا' }
  ],
  africa: [
    { city: 'Nairobi', country: 'Kenya / Kenya / Kenia / 肯尼亚 / كينيا' },
    { city: 'Bujumbura', country: 'Burundi / Burundi / Burundi / 布隆迪 / بوروندي' },
    { city: 'Kinshasa', country: 'RD Congo / DR Congo / RD Congo / 刚果民主共和国 / الكونغو الديمقراطية' },
    { city: 'Kampala', country: 'Ouganda / Uganda / Uganda / 乌干达 / أوغندا' },
    { city: 'Casablanca', country: 'Maroc / Morocco / Marruecos / 摩洛哥 / المغرب' },
    { city: 'Lagos', country: 'Nigeria / Nigeria / Nigeria / 尼日利亚 / نيجيريا' },
    { city: 'Cape Town', country: 'Afrique du Sud / South Africa / Sudáfrica / 南非 / جنوب أفريقيا' }
  ]
};
